/**
 * GraphNodePanel — Right sidebar for editing properties of a selected graph node.
 *
 * Appears when a node is clicked in the GraphBuilder canvas. Shows editable
 * fields appropriate for the node's type (agent, router, orchestrator, function).
 */
import React, { useState, useEffect } from 'react';
import { X, Bot, GitBranch, Cpu, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { GraphNodeConfig } from './GraphBuilder';
import type { AvailableResources } from '@/services/agentWizardApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GraphNodePanelProps {
  nodeId: string;
  nodeConfig: GraphNodeConfig;
  onUpdate: (config: GraphNodeConfig) => void;
  onClose: () => void;
  resources?: AvailableResources | null;
}

const NODE_TYPE_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  agent: { icon: <Bot className="h-4 w-4" />, color: 'text-blue-500', label: 'Agent' },
  router: { icon: <GitBranch className="h-4 w-4" />, color: 'text-amber-500', label: 'Router' },
  orchestrator: { icon: <Cpu className="h-4 w-4" />, color: 'text-purple-500', label: 'Orchestrator' },
  function: { icon: <Zap className="h-4 w-4" />, color: 'text-green-500', label: 'Function' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GraphNodePanel({
  nodeId,
  nodeConfig,
  onUpdate,
  onClose,
  resources,
}: GraphNodePanelProps) {
  // Local form state, syncs on commit (blur / Enter)
  const [local, setLocal] = useState<GraphNodeConfig>({ ...nodeConfig });

  // Reset when a different node is selected
  useEffect(() => {
    setLocal({ ...nodeConfig });
  }, [nodeId]);

  const commit = (override?: Partial<GraphNodeConfig>) => {
    const merged = { ...local, ...override };
    onUpdate(merged);
  };

  const meta = NODE_TYPE_META[local.type] ?? NODE_TYPE_META.agent;

  const availableTools = resources?.tools ?? [];
  const availableModels = resources?.models ?? [
    { id: 'openrouter:google/gemini-3-flash-preview', name: 'Gemini 3 Flash', provider: 'Google' },
    { id: 'openrouter:anthropic/claude-sonnet-4.5', name: 'Claude Sonnet 4.5', provider: 'Anthropic' },
    { id: 'openrouter:openai/gpt-5.2', name: 'GPT-5.2', provider: 'OpenAI' },
  ];
  const availableMcpServers = resources?.mcp_servers ?? [];

  const toggleTool = (toolName: string) => {
    const current = local.tools ?? [];
    const next = current.includes(toolName)
      ? current.filter((t) => t !== toolName)
      : [...current, toolName];
    setLocal((prev) => ({ ...prev, tools: next }));
    commit({ tools: next });
  };

  const toggleMcpServer = (name: string) => {
    const current = local.mcp_servers ?? [];
    const next = current.includes(name)
      ? current.filter((s) => s !== name)
      : [...current, name];
    setLocal((prev) => ({ ...prev, mcp_servers: next }));
    commit({ mcp_servers: next });
  };

  return (
    <div className="w-72 border-l bg-background flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2">
          <span className={meta.color}>{meta.icon}</span>
          <span className="text-sm font-medium">{meta.label} Node</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 px-3 py-3">
        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="node-name" className="text-xs">Name</Label>
            <Input
              id="node-name"
              value={local.name}
              onChange={(e) => setLocal((p) => ({ ...p, name: e.target.value }))}
              onBlur={() => commit()}
              className="h-8 text-sm"
            />
          </div>

          {/* Type */}
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <Select
              value={local.type}
              onValueChange={(v) => {
                setLocal((p) => ({ ...p, type: v }));
                commit({ type: v });
              }}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="router">Router</SelectItem>
                <SelectItem value="orchestrator">Orchestrator</SelectItem>
                <SelectItem value="function">Function</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Model (agent/orchestrator) */}
          {(local.type === 'agent' || local.type === 'orchestrator') && (
            <div className="space-y-1.5">
              <Label className="text-xs">Model</Label>
              <Select
                value={local.model ?? ''}
                onValueChange={(v) => {
                  setLocal((p) => ({ ...p, model: v || undefined }));
                  commit({ model: v || undefined });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Inherit default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Inherit default</SelectItem>
                  {availableModels.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Prompt (agent/orchestrator) */}
          {(local.type === 'agent' || local.type === 'orchestrator') && (
            <div className="space-y-1.5">
              <Label htmlFor="node-prompt" className="text-xs">System Prompt</Label>
              <Textarea
                id="node-prompt"
                value={local.prompt ?? ''}
                onChange={(e) => setLocal((p) => ({ ...p, prompt: e.target.value }))}
                onBlur={() => commit()}
                rows={5}
                className="text-xs resize-y"
                placeholder="You are a helpful assistant..."
              />
            </div>
          )}

          {/* Function name (function type) */}
          {local.type === 'function' && (
            <div className="space-y-1.5">
              <Label htmlFor="node-function" className="text-xs">Function Name</Label>
              <Input
                id="node-function"
                value={local.function ?? ''}
                onChange={(e) => setLocal((p) => ({ ...p, function: e.target.value }))}
                onBlur={() => commit()}
                className="h-8 text-sm font-mono"
                placeholder="my_function"
              />
            </div>
          )}

          {/* Router name (router type) */}
          {local.type === 'router' && (
            <div className="space-y-1.5">
              <Label htmlFor="node-router" className="text-xs">Router Name</Label>
              <Input
                id="node-router"
                value={local.router ?? ''}
                onChange={(e) => setLocal((p) => ({ ...p, router: e.target.value }))}
                onBlur={() => commit()}
                className="h-8 text-sm"
                placeholder="conditional_router"
              />
            </div>
          )}

          {/* Workers (orchestrator type) */}
          {local.type === 'orchestrator' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Workers</Label>
              <div className="flex flex-wrap gap-1">
                {(local.workers ?? []).map((w, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="text-xs cursor-pointer"
                    onClick={() => {
                      const next = (local.workers ?? []).filter((_, idx) => idx !== i);
                      setLocal((p) => ({ ...p, workers: next }));
                      commit({ workers: next });
                    }}
                  >
                    {w.name} &times;
                  </Badge>
                ))}
              </div>
              <div className="flex gap-1">
                <Input
                  id="add-worker"
                  className="h-7 text-xs flex-1"
                  placeholder="Worker name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val) {
                        const next = [...(local.workers ?? []), { name: val }];
                        setLocal((p) => ({ ...p, workers: next }));
                        commit({ workers: next });
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          <Separator />

          {/* Tools (agent/orchestrator) */}
          {(local.type === 'agent' || local.type === 'orchestrator') && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Tools</Label>
              {availableTools.length > 0 ? (
                <div className="space-y-1">
                  {availableTools.map((tool) => (
                    <label
                      key={tool.name}
                      className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-muted/50 text-xs"
                    >
                      <Switch
                        checked={(local.tools ?? []).includes(tool.name)}
                        onCheckedChange={() => toggleTool(tool.name)}
                        className="scale-75"
                      />
                      <span className="truncate">{tool.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No tools available</p>
              )}
            </div>
          )}

          {/* MCP Servers */}
          {(local.type === 'agent' || local.type === 'orchestrator') && availableMcpServers.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">MCP Servers</Label>
              <div className="space-y-1">
                {availableMcpServers.map((srv) => (
                  <label
                    key={srv.name}
                    className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-muted/50 text-xs"
                  >
                    <Switch
                      checked={(local.mcp_servers ?? []).includes(srv.name)}
                      onCheckedChange={() => toggleMcpServer(srv.name)}
                      className="scale-75"
                    />
                    <span className="truncate">{srv.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* HITL toggles */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Human-in-the-Loop</Label>
            <div className="flex items-center justify-between">
              <span className="text-xs">Pause before</span>
              <Switch
                checked={local.hitl_before ?? false}
                onCheckedChange={(v) => {
                  setLocal((p) => ({ ...p, hitl_before: v }));
                  commit({ hitl_before: v });
                }}
                className="scale-75"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs">Pause after</span>
              <Switch
                checked={local.hitl_after ?? false}
                onCheckedChange={(v) => {
                  setLocal((p) => ({ ...p, hitl_after: v }));
                  commit({ hitl_after: v });
                }}
                className="scale-75"
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default GraphNodePanel;
